import { Children, isValidElement, ReactElement } from 'react';
import { ApiGetCompetitionsResponse200Type } from 'src/api_openapi/v2/trading-search/apiGetCompetitions';
import { ApiGetEventsResponse200Type } from 'src/api_openapi/v2/trading-search/apiGetEvents';
import { ApiGetMarketsResponse200Type } from 'src/api_openapi/v2/trading-search/apiGetMarkets';
import { ApiGetSportsResponse200Type } from 'src/api_openapi/v2/trading-search/apiGetSports';
import { jsonParse } from 'src_common/common/jsonParse';
import { z } from 'zod';

export const FIRST_PAGE = 1;
export const PER_PAGE = 200;

export interface TreeType {
    id: string;
    value: string;
    label: string;
    showCheckbox?: boolean;
    children?: TreeType[];
}

export interface BaseTreeType {
    id: string;
    value: string;
    label: string;
    showCheckbox?: boolean;
    children?: SportsPickerTreeType[];
}

export interface EventTreeType {
    id: string;
    value: string;
    name: string;
    label: ReactElement;
    showCheckbox?: boolean;
    children?: TreeType[];
}

export type SportsPickerTreeType = BaseTreeType | EventTreeType | TreeType;

export type EventType = ApiGetEventsResponse200Type['items'][number];
export type CompetitionType = ApiGetCompetitionsResponse200Type['items'][number];
export type MarketType = ApiGetMarketsResponse200Type['items'][number];

export const nodeValueZodSchema = z.union([
    z.object({
        type: z.literal('sport'),
        sportId: z.string(),
    }),
    z.object({
        type: z.literal('competition'),
        sportId: z.string(),
        competitionId: z.string(),
    }),
    z.object({
        type: z.literal('event'),
        sportId: z.string(),
        competitionId: z.string(),
        eventId: z.string(),
    }),
    z.object({
        type: z.literal('market'),
        sportId: z.string(),
        competitionId: z.string(),
        eventId: z.string(),
        marketId: z.string(),
    }),
]);

export type NodeValueType = z.infer<typeof nodeValueZodSchema>;

export type InitValuesType = {
    sportIds: Array<string>;
    competitionIds: Array<string>;
    eventIds: Array<string>;
    marketIds: Array<string>;
};

export const serializeNodeValue = (value: NodeValueType): string => {
    return JSON.stringify(value);
};

export const deserializeNodeValue = (serializedValue: string): NodeValueType | null => {
    const deserialized = jsonParse(serializedValue);

    if (deserialized.type === 'text') {
        console.warn('deserializeNodeValue -> json parse error for value: ', deserialized);
        return null;
    }

    const parsed = nodeValueZodSchema.safeParse(deserialized.json);

    if (parsed.success === false) {
        console.warn('deserializeNodeValue -> ', parsed.error);
        return null;
    }

    return parsed.data;
};

// Temp. solution due Sportsbook API returns ids as names
const sportIdToNameMap: Map<string, string> = new Map([
    ['snooker', 'Snooker'],
    ['handball', 'Handball'],
    ['golf', 'Golf'],
    ['horseracingantepost', 'Horse Racing AntePost'],
    ['formulaone', 'Formula One'],
    ['greyhoundracing', 'Greyhound Racing'],
    ['rugbyunion', 'Rugby Union'],
    ['internationalhorseracing', 'International Horse Racing'],
    ['horseracing', 'Horse Racing'],
    ['volleyball', 'Volleyball'],
    ['tennisdoubles', 'Tennis Doubles'],
    ['icehockey', 'Ice Hockey'],
    ['basketball', 'Basketball'],
    ['tvspecials', 'TV & Specials'],
    ['australianrules', 'Australian Rules'],
    ['gaelicfootball', 'Gaelic Football'],
    ['motorsport', 'Motor Sport'],
    ['politics', 'Politics'],
    ['badminton', 'Badminton'],
    ['rugbyleague', 'Rugby League'],
    ['football', 'Football'],
    ['tabletennis', 'Table Tennis'],
    ['cricket', 'Cricket'],
    ['cycling', 'Cycling'],
    ['mixedmartialarts', 'Mixed Martial Arts'],
    ['tennis', 'Tennis'],
    ['olympics', 'Olympics'],
    ['esports', 'Esports'],
    ['gaelichurling', 'Gaelic Hurling'],
    ['boxing', 'Boxing'],
    ['darts', 'Darts'],
    ['americanfootball', 'American Football'],
    ['baseball', 'Baseball'],
]);

export const changeIdToName = (sports: ApiGetSportsResponse200Type): ApiGetSportsResponse200Type => {
    const renamedItems = sports.items.map((item) => {
        const newName = sportIdToNameMap.get(item.sportId) ?? item.name;
        return {
            sportId: item.sportId,
            name: newName,
        };
    });

    return {
        items: renamedItems,
        pagination: sports.pagination,
    };
};

export const sortSportsAlphabetically = (sports: ApiGetSportsResponse200Type): ApiGetSportsResponse200Type => {
    const sortedItems = sports.items.sort((a, b) => {
        const aValue = a.name;
        const bValue = b.name;

        if (aValue > bValue) {
            return 1;
        }
        if (aValue < bValue) {
            return -1;
        }
        return 0;
    });
    return {
        items: sortedItems,
        pagination: sports.pagination,
    };
};

export const isEventTreeType = (item: SportsPickerTreeType): item is EventTreeType =>
    'name' in item && typeof item.name === 'string';

export const isTreeType = (node: SportsPickerTreeType): node is TreeType => {
    return typeof node.label === 'string' && !('name' in node);
};

export const getDisplayLabel = (item: SportsPickerTreeType): string => {
    const getTextContentElements = (label: ReactElement): string[] => {
        const result: string[] = [];

        Children.forEach(label.props.children, (child) => {
            if (typeof child === 'string') {
                result.push(child);
            } else if (isValidElement(child)) {
                result.push(...getTextContentElements(child));
            }
        });

        return result;
    };

    return isEventTreeType(item) ? getTextContentElements(item.label).join('') : item.label;
};
